import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Swal from 'sweetalert2';

// Imports Standalone
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Popover, PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

import { tzConvertUTC2Local } from 'src/assets/convertTimeZone/convertTimeZone';
import { ErrorLibService } from 'src/shared/services/error-lib.service';
import { ServiceOrderFilterParams, ServiceOrderService, ServiceOrderSortField } from 'src/shared/services/service-order.service';
import { PageHeaderComponent } from 'src/shared/components/page-header/page-header.component';
import { Unsubscriber } from '../unsubscriber/unsubscriber.component';
import { ServiceOrderCreateDialogComponent } from './create-dialog/service-order-create-dialog.component';
import { FinishServiceOrderPayload, ServiceOrderFinishDialogComponent } from './finish-dialog/service-order-finish-dialog.component';
import { ServiceOrderDialogComponent } from './dialog/service-order-dialog.component';
import {
  Installation,
  ServiceOrder,
  User,
  asUtcIso,
  mapUserNode,
  shortDate,
  toFloat,
  toInstallationGlobalId,
  toNumericUserId,
  userDisplayName,
} from './models/service-order.model';
import { DetailSection, buildDetailSections } from './models/service-order.sections';
import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  SelectOption,
  buildSelectOptions,
  isClosedStatus,
  priorityMeta,
  statusMeta,
} from './models/service-order.labels';

/** Escopos da busca: `serialNumber` é traduzido para referência da instalação antes da consulta **/
type SearchScope = 'reference' | 'installationReference' | 'serialNumber';

/** Chip de filtro que fica atrás de "Mais filtros" **/
interface HiddenFilterChip {
  control: 'priority' | 'selectedUser';
  labelKey: string;
  valueKey: string | null;
  text: string | null;
}

/** Quantidade de OS buscadas por página na geração de CSV/PDF **/
const FILE_PAGE_SIZE = 100;

@Component({
  selector: 'app-service-order',
  templateUrl: './service-order.component.html',
  styleUrls: ['./service-order.component.less'],
  imports: [
    CommonModule,
    PageHeaderComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    AutoCompleteModule,
    ButtonModule,
    InputTextModule,
    PopoverModule,
    ProgressSpinnerModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    ServiceOrderCreateDialogComponent,
    ServiceOrderDialogComponent,
    ServiceOrderFinishDialogComponent,
  ],
  // MessageService da tela: o toast é local, sem host global no app
  providers: [MessageService],
})
export class ServiceOrderComponent extends Unsubscriber implements OnInit, OnDestroy {
  constructor(
    private serviceOrderService: ServiceOrderService,
    private formBuilder: FormBuilder,
    private errorLibService: ErrorLibService,
    private translate: TranslateService,
    private messageService: MessageService,
  ) {
    super();

    this.buildSelectOptions();
    // Os labels são traduzidos na montagem das opções, então trocar de idioma precisa refazê-las
    this.anotherSubscription = this.translate.onLangChange.subscribe(() => this.buildSelectOptions());
  }

  private buildSelectOptions(): void {
    this.statusOptions = buildSelectOptions(STATUS_OPTIONS, statusMeta, (key) => this.translate.instant(key));
    this.priorityOptions = buildSelectOptions(PRIORITY_OPTIONS, priorityMeta, (key) => this.translate.instant(key));
  }

  ngOnInit(): void {
    // A tabela ocupa a tela inteira: abrir a tela vazia esperando um clique em Filtrar
    // deixaria a página com cara de quebrada
    this.loadPage(0, true);
  }

  public company: any | null = localStorage.getItem('lastCompanySelected')
    ? localStorage.getItem('lastCompanySelected')
    : null;

  public createDialogOpen: boolean = false;

  /** Sugestões do autocomplete de responsável no filtro **/
  public filterUserList: User[] = [];
  public userFilterLoading: boolean = false;

  public ELEMENT_DATA: ServiceOrder[] = [];

  /** OS aberta na Ficha da OS (modal de leitura + edição) **/
  public selectedServiceOrder: ServiceOrder | null = null;

  /** OS aberta no dialog Finalizar OS (pela tabela ou pela Ficha) **/
  public finishTarget: ServiceOrder | null = null;
  public finishLoading: boolean = false;
  public openInEditMode: boolean = false;

  public statusOptions: SelectOption[] = [];

  public priorityOptions: SelectOption[] = [];

  /**
   * Valor "vazio" de cada controle do filtro. Os <select> nativos usam string vazia porque
   * com `null` o Angular não casa nenhuma <option> e o campo fica em branco em vez de "Todas".
   **/
  private readonly emptyFilter: Record<string, string | null> = {
    search: null,
    status: '',
    priority: '',
    selectedUser: null,
  };

  /**
   * ID da OS e Instalação são igualdade exata no backend, então viraram um campo só com um
   * seletor de escopo: envia exatamente um dos dois parâmetros de hoje.
   **/
  public filterForm: FormGroup = this.formBuilder.group({
    searchScope: ['reference' as SearchScope],
    search: [null],
    status: [''],
    priority: [''],
    selectedUser: [null],
  });

  /** Estado do popover "Mais filtros", só para o aria-expanded do botão **/
  public moreFiltersOpen: boolean = false;

  /** Filtro efetivamente aplicado: a paginação reusa esse snapshot **/
  private appliedFilter: ServiceOrderFilterParams = {};

  /** Serial digitado não corresponde a nenhuma instalação: lista vazia sem consultar OS **/
  private serialUnresolved: boolean = false;

  //Variáveis para controle de loading
  public filterLoading: boolean = false;
  public fileLoading: boolean = false;

  /** Ordenação da tabela (só colunas que o alerts-api ordena). Padrão: mais recentes primeiro **/
  public sortField: string = 'createdAt';
  public sortOrder: number = -1;

  private static readonly SORT_FIELDS: Record<string, ServiceOrderSortField> = {
    createdAt: 'CREATED_AT',
    startedAt: 'STARTED_AT',
    closedAt: 'CLOSED_AT',
    reference: 'REFERENCE',
  };

  /** Variáveis utilizadas para manipulação do Paginator **/
  public pageIndex: number = 0;
  public pageSize: number = 10;
  public readonly pageSizeOptions: number[] = [10, 25, 50];
  public pageSizeMenuOpen: boolean = false;
  public length: number = 0;
  /** A API não devolve total: só é fato na última página (sem linha-sonda além dela) **/
  public hasNextPage: boolean = false;

  /** Variáveis utilizadas para geração e manipulação dos arquivos PDF e CSV **/
  public FILE_DATA: ServiceOrder[] = [];
  public isReady = false;

  /** Marca que já houve pelo menos uma consulta, para diferenciar vazio de não carregado **/
  public hasLoadedOnce: boolean = false;

  /** Corta texto longo com elipse: sem ela o corte terminava no meio da palavra sem sinal **/
  public truncate(text: string | null, max: number): string {
    if (!text) {
      return '';
    }

    return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
  }

  public statusMeta = statusMeta;
  public priorityMeta = priorityMeta;
  public isClosedStatus = isClosedStatus;
  public shortDate = shortDate;

  // ------------------------------------------------------- cartão de resumo

  /** Badge de status só com ícone (CSS abaixo de 1000px): o rótulo vai para o tooltip **/
  public get compactStatus(): boolean {
    return window.innerWidth <= ServiceOrderComponent.COMPACT_STATUS_MAX_WIDTH;
  }

  private static readonly COMPACT_STATUS_MAX_WIDTH = 1000;

  /** Só para a detecção de mudanças reavaliar `compactStatus` ao redimensionar **/
  @HostListener('window:resize')
  public onWindowResize(): void {}

  /** Popover único da tabela: o conteúdo troca conforme a OS sob o mouse **/
  @ViewChild('peek') private peek?: Popover;

  /** OS exibida no cartão de resumo (hover no ID) **/
  public peekServiceOrder: ServiceOrder | null = null;

  private peekShowTimer: ReturnType<typeof setTimeout> | null = null;

  private peekHideTimer: ReturnType<typeof setTimeout> | null = null;

  /** Atraso para não piscar cartão ao cruzar a tabela com o mouse **/
  private static readonly PEEK_SHOW_DELAY = 400;

  /** Folga para o mouse sair do ID e entrar no cartão sem ele fechar **/
  private static readonly PEEK_HIDE_DELAY = 150;

  /** Seções da OS espiada, as mesmas da Ficha: vazias mostram o placeholder **/
  public get peekSections(): DetailSection[] {
    return buildDetailSections(this.peekServiceOrder);
  }

  /** Cartão pedido enquanto outro ainda fecha: abre quando o fechamento terminar (onHide) **/
  private pendingPeek: { event: MouseEvent; target: HTMLElement; serviceOrder: ServiceOrder } | null = null;

  /**
   * Hover no ID: agenda o cartão de qualquer OS (seções vazias mostram o placeholder). Com a
   * Ficha aberta não abre. Com outro cartão aberto, fecha primeiro e reabre no novo alvo quando
   * a animação terminar: o p-popover é um só, e chamar show() com ele visível trocava o
   * conteúdo sem realinhar a posição.
   **/
  public schedulePeek(event: MouseEvent, serviceOrder: ServiceOrder): void {
    this.clearPeekTimers();
    this.pendingPeek = null;

    if (this.selectedServiceOrder) {
      return;
    }

    // Mesmo cartão já aberto: só cancela o fechamento pendente
    if (this.peekServiceOrder === serviceOrder && this.peek?.overlayVisible) {
      return;
    }

    const target = event.currentTarget as HTMLElement;

    this.peekShowTimer = setTimeout(() => {
      this.peekShowTimer = null;

      if (this.peek?.overlayVisible) {
        this.pendingPeek = { event, target, serviceOrder };
        this.peek.hide();
        return;
      }

      this.peekServiceOrder = serviceOrder;
      this.peek?.show(event, target);
    }, ServiceOrderComponent.PEEK_SHOW_DELAY);
  }

  /**
   * onHide do popover. Se havia um cartão na fila, abre no próximo tick: o PrimeNG ainda zera
   * `render` logo depois de emitir onHide, e um show() síncrono seria desfeito.
   **/
  public onPeekHidden(): void {
    const pending = this.pendingPeek;

    this.pendingPeek = null;
    this.peekServiceOrder = null;

    if (!pending) {
      return;
    }

    this.peekShowTimer = setTimeout(() => {
      this.peekShowTimer = null;
      this.peekServiceOrder = pending.serviceOrder;
      this.peek?.show(pending.event, pending.target);
    }, 0);
  }

  /** Mouse saiu do ID: fecha com folga (evita piscar ao cruzar a borda entre linhas) **/
  public cancelPeek(): void {
    this.clearPeekTimers();
    this.pendingPeek = null;

    this.peekHideTimer = setTimeout(() => {
      this.peekHideTimer = null;
      this.hidePeek();
    }, ServiceOrderComponent.PEEK_HIDE_DELAY);
  }

  public hidePeek(): void {
    this.clearPeekTimers();
    this.pendingPeek = null;

    if (this.peekServiceOrder || this.peek?.overlayVisible) {
      this.peekServiceOrder = null;
      this.peek?.hide();
    }
  }

  private clearPeekTimers(): void {
    if (this.peekShowTimer) {
      clearTimeout(this.peekShowTimer);
      this.peekShowTimer = null;
    }

    if (this.peekHideTimer) {
      clearTimeout(this.peekHideTimer);
      this.peekHideTimer = null;
    }
  }

  override ngOnDestroy(): void {
    this.clearPeekTimers();
    super.ngOnDestroy();
  }

  /** Clique no cabeçalho: ordena no servidor e volta para a primeira página **/
  public sortChanged(event: { field: string; order: number }): void {
    if (!ServiceOrderComponent.SORT_FIELDS[event.field]) {
      return;
    }

    // O p-table reemite onSort quando [sortOrder] volta pra ele via ngOnChanges:
    // sem este guarda cada clique dispararia duas consultas idênticas
    if (event.field === this.sortField && event.order === this.sortOrder) {
      return;
    }

    this.sortField = event.field;
    this.sortOrder = event.order;
    this.pageIndex = 0;
    this.loadPage(0, false);
  }

  private get sortParams(): Pick<ServiceOrderFilterParams, 'sortBy' | 'sortOrder'> {
    return {
      sortBy: ServiceOrderComponent.SORT_FIELDS[this.sortField] ?? 'CREATED_AT',
      sortOrder: this.sortOrder === 1 ? 'ASC' : 'DESC',
    };
  }

  /** Paginador: anterior/próxima **/
  public pageChanged(event: { first: number; rows: number }) {
    this.pageIndex = Math.floor(event.first / event.rows);

    this.loadPage(event.first, false);
  }

  /** Tamanho da página (10/25/50) escolhido no contador da barra: volta para a primeira página **/
  public changePageSize(size: number): void {
    if (!this.pageSizeOptions.includes(size) || size === this.pageSize) {
      return;
    }

    this.pageSize = size;
    this.pageIndex = 0;
    this.loadPage(0, false);
  }

  public openCreateDialog(): void {
    this.createDialogOpen = true;
  }

  public closeCreateDialog(): void {
    this.createDialogOpen = false;
  }

  /** Nova busca: captura o formulário no snapshot e volta para a primeira página **/
  public filterServiceOrder(offset: number = 0): void {
    if (offset === 0) {
      const value = this.filterForm.value;
      const search = (value?.search ?? '').trim() || null;

      this.appliedFilter = {
        reference: value?.searchScope === 'reference' ? search : null,
        installationReference: value?.searchScope === 'installationReference' ? search : null,
        status: value?.status || null,
        priority: value?.priority || null,
        // id numérico: o bifrost atual não decodifica o ID relay antes de chamar o alerts-api
        assignee: toNumericUserId(value?.selectedUser?.id),
      };
      this.pageIndex = 0;
      this.serialUnresolved = false;

      if (value?.searchScope === 'serialNumber' && search) {
        this.resolveSerialAndLoad(search);
        return;
      }
    }

    this.loadPage(offset, false);
  }

  /**
   * A consulta de OS só filtra por referência da instalação; o serial do equipamento (o que o
   * operador lê na etiqueta) é traduzido antes. Sem instalação para o serial, a lista fica vazia
   * sem ir ao servidor — mandar `installationReference: null` traria todas as OS.
   **/
  private resolveSerialAndLoad(serial: string): void {
    this.filterLoading = true;

    this.anotherSubscription = this.serviceOrderService.findInstallationBySerial(serial).subscribe({
      next: (installation: Installation | null) => {
        if (installation?.reference) {
          this.appliedFilter = { ...this.appliedFilter, installationReference: installation.reference };
        } else {
          this.serialUnresolved = true;
        }

        this.loadPage(0, false);
      },
      error: (error: any) => {
        this.filterLoading = false;
        this.hasLoadedOnce = true;
        this.errorLibService.errorAlert(error);
      },
    });
  }

  /** Recarrega a página atual sem alertar lista vazia (pós criação/edição/transição) **/
  private refreshCurrentPage(): void {
    this.loadPage(this.pageIndex * this.pageSize, true);
  }

  /**
   * Executa a consulta de fato. O snapshot `appliedFilter` garante que paginar depois de
   * editar um campo do filtro não aplique o filtro novo em um offset antigo.
   **/
  private loadPage(offset: number, silent: boolean): void {
    if (this.serialUnresolved) {
      this.ELEMENT_DATA = [];
      this.length = 0;
      this.hasLoadedOnce = true;
      this.filterLoading = false;

      if (!silent) {
        this.noResultsAlert();
      }

      return;
    }

    this.filterLoading = true;

    // Pede uma linha além da página: a API não devolve total, e o cálculo anterior
    // (offset + pageSize + 1) inventava uma próxima página que voltava vazia e sumia
    // com a tabela. Com a linha-sonda o "tem próxima" passa a ser um fato.
    this.anotherSubscription = this.serviceOrderService
      .filterServiceOrder({ ...this.appliedFilter, ...this.sortParams, offset, limit: this.pageSize + 1 })
      .valueChanges.subscribe({
        next: (response: any) => {
          // Validando se a resposta da API está carregando ou se os dados estão disponíveis para evitar erros de renderização
          if (response.loading || !response?.data) {
            return;
          }

          const rows: ServiceOrder[] = (response?.data?.serviceOrder?.edges ?? []).map(
            (edge: any) => this.mapServiceOrder(edge?.node, true),
          );

          const hasNextPage = rows.length > this.pageSize;

          this.ELEMENT_DATA = hasNextPage ? rows.slice(0, this.pageSize) : rows;
          this.hasNextPage = hasNextPage;
          // Placeholder pro paginador habilitar "próxima"; não é o total real
          this.length = offset + this.ELEMENT_DATA.length + (hasNextPage ? 1 : 0);

          // Página além do fim (dado mudou entre requisições): volta uma página
          // em vez de deixar a tabela vazia sem paginador
          if (!this.ELEMENT_DATA.length && offset > 0) {
            const previousOffset = Math.max(0, offset - this.pageSize);

            this.pageIndex = previousOffset / this.pageSize;
            this.loadPage(previousOffset, true);
            return;
          }

          if (!this.ELEMENT_DATA.length && !silent) {
            this.noResultsAlert();
          }

          this.hasLoadedOnce = true;
          this.filterLoading = false;
        },
        error: (error: any) => {
          this.hasLoadedOnce = true;
          this.filterLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  /** Converte o node da API para o modelo da tela **/
  private mapServiceOrder(node: any, convertTimeZone: boolean): ServiceOrder {
    // Datas do alerts-api vêm sem offset: asUtcIso marca como UTC antes do fuso do usuário
    const toLocal = (value: string | null) =>
      value && convertTimeZone ? tzConvertUTC2Local(asUtcIso(value) as string) : value ?? null;

    return new ServiceOrder(
      node?.id,
      node?.reference,
      mapUserNode(node?.user),
      node?.status,
      new Installation(
        toInstallationGlobalId(node?.installationId) as string,
        node?.installationReference,
        toFloat(node?.latitude),
        toFloat(node?.longitude),
        node?.deviceMac,
      ),
      node?.priority,
      toLocal(node?.createdAt),
      node?.assignee?.id ? mapUserNode(node?.assignee) : null,
      node?.problems ?? null,
      node?.notes ?? null,
      node?.closeNotes ?? null,
      toLocal(node?.startedAt),
      toLocal(node?.closedAt),
      node?.chAlertId ?? null,
    );
  }

  /** Abre a Ficha da OS em leitura (clique na linha) ou já em edição (lápis) **/
  public openDialog(serviceOrder: ServiceOrder, edit: boolean): void {
    this.hidePeek();
    this.selectedServiceOrder = serviceOrder;
    this.openInEditMode = edit;
  }

  public closeDialog(): void {
    this.selectedServiceOrder = null;
    this.openInEditMode = false;
  }

  /** Posição da OS aberta dentro da página atual, para navegar sem fechar o modal **/
  private get selectedIndex(): number {
    return this.selectedServiceOrder ? this.ELEMENT_DATA.indexOf(this.selectedServiceOrder) : -1;
  }

  /** Posição 1-based da OS aberta, para o "n de N" do rodapé do modal **/
  public get selectedPosition(): number {
    return this.selectedIndex + 1;
  }

  public get hasPreviousServiceOrder(): boolean {
    return this.selectedIndex > 0;
  }

  public get hasNextServiceOrder(): boolean {
    const index = this.selectedIndex;

    return index >= 0 && index < this.ELEMENT_DATA.length - 1;
  }

  public showPreviousServiceOrder(): void {
    if (this.hasPreviousServiceOrder) {
      this.openDialog(this.ELEMENT_DATA[this.selectedIndex - 1], false);
    }
  }

  public showNextServiceOrder(): void {
    if (this.hasNextServiceOrder) {
      this.openDialog(this.ELEMENT_DATA[this.selectedIndex + 1], false);
    }
  }

  public onDetailSaved(): void {
    this.refreshCurrentPage();
  }

  /** OS criada: recarrega e mostra a referência (informada ou gerada pelo servidor) **/
  public onCreated(reference: string | null): void {
    // Primeira página na ordenação padrão (mais recentes primeiro): a OS nova aparece no topo
    // mesmo que o usuário tivesse invertido a coluna antes de criar
    this.sortField = 'createdAt';
    this.sortOrder = -1;
    this.pageIndex = 0;
    this.loadPage(0, true);

    this.messageService.add({
      severity: 'success',
      summary: reference
        ? this.translate.instant('service-order.created-toast', { reference })
        : this.translate.instant('service-order.created-toast-no-reference'),
      life: 5000,
    });
  }

  // Carrega a lista de usuários do autocomplete de responsável no filtro
  public searchUser(event: { query: string }): void {
    this.userFilterLoading = true;

    this.anotherSubscription = this.serviceOrderService
      .searchUsers(event?.query)
      .subscribe({
        next: (users: User[]) => {
          this.filterUserList = users;
          this.userFilterLoading = false;
        },
        error: (error: any) => {
          this.userFilterLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  // Inicia a ordem de Serviço
  public startServiceOrder(id: any) {
    Swal.fire({
      title: 'Tem certeza que deseja iniciar a ordem de serviço?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        this.anotherSubscription = this.serviceOrderService
          .startServiceOrder(id)
          .subscribe({
            // Resultado em toast: o Swal de "ok" era um clique a mais depois da confirmação
            next: () => {
              this.actionToast('success', 'service-order.started-toast');
              this.closeDialog();
              this.refreshCurrentPage();
            },
            error: () => this.actionToast('error', 'service-order.start-error-toast'),
          });
      }
    });
  }

  /**
   * Finalizar OS abre o dialog próprio (Proposta F), que já é a confirmação: detalhes e a
   * marcação "Com pendência" no mesmo passo. Chega pela tabela ou pela Ficha, sempre por id.
   */
  public closeServiceOrder(id: any) {
    const target = this.ELEMENT_DATA.find((serviceOrder) => serviceOrder.id === id)
      ?? (this.selectedServiceOrder?.id === id ? this.selectedServiceOrder : null);

    if (!target) {
      return;
    }

    this.hidePeek();
    this.finishTarget = target;
  }

  public cancelFinish(): void {
    this.finishTarget = null;
  }

  public confirmFinish(payload: FinishServiceOrderPayload): void {
    if (!this.finishTarget || this.finishLoading) {
      return;
    }

    this.finishLoading = true;

    this.anotherSubscription = this.serviceOrderService
      // O alerts-api grava closed_at, close_notes e o status final numa chamada só
      .closeServiceOrder(this.finishTarget.id, payload.closeNotes, payload.withIssues)
      .subscribe({
        next: () => {
          this.finishLoading = false;
          this.finishTarget = null;
          this.actionToast('success', payload.withIssues ? 'service-order.closed-with-issues-toast' : 'service-order.closed-toast');
          this.closeDialog();
          this.refreshCurrentPage();
        },
        error: () => {
          this.finishLoading = false;
          this.actionToast('error', 'service-order.close-error-toast');
        },
      });
  }

  /** Resultado de iniciar/fechar em toast, no mesmo canto dos demais avisos da tela **/
  private actionToast(severity: 'success' | 'error', messageKey: string): void {
    this.messageService.add({
      severity,
      summary: this.translate.instant(messageKey),
      life: 5000,
    });
  }

  // ------------------------------------------------------------- barra de filtro

  /** Busca aplica no Enter (ou no ícone), não em botão Filtrar **/
  public applySearch(): void {
    this.filterServiceOrder(0);
  }

  public clearSearch(): void {
    this.filterForm.get('search')?.setValue(null);
    this.filterServiceOrder(0);
  }

  /** Trocar o escopo com texto digitado refaz a busca no outro campo **/
  public onScopeChange(): void {
    if ((this.filterForm.value?.search ?? '').trim()) {
      this.filterServiceOrder(0);
    }
  }

  /** Status é grupo de botões: um clique aplica **/
  public setStatus(status: string): void {
    this.filterForm.get('status')?.setValue(status);
    this.filterServiceOrder(0);
  }

  /** Prioridade e técnico (no popover) aplicam ao mudar **/
  public applyHiddenFilters(): void {
    this.filterServiceOrder(0);
  }

  public get hiddenFilterCount(): number {
    return this.activeHiddenFilters.length;
  }

  /** Filtros escondidos atrás de "Mais filtros" que estão aplicados: virem badge e chips **/
  public get activeHiddenFilters(): HiddenFilterChip[] {
    const chips: HiddenFilterChip[] = [];

    if (this.appliedFilter.priority) {
      chips.push({
        control: 'priority',
        labelKey: 'service-order.priority-label',
        valueKey: priorityMeta(this.appliedFilter.priority).labelKey,
        text: null,
      });
    }

    if (this.appliedFilter.assignee) {
      chips.push({
        control: 'selectedUser',
        labelKey: 'service-order.assigned-to',
        valueKey: null,
        text: userDisplayName(this.filterForm.value?.selectedUser),
      });
    }

    return chips;
  }

  public removeHiddenFilter(control: 'priority' | 'selectedUser'): void {
    this.filterForm.get(control)?.reset(this.emptyFilter[control] ?? null);
    this.filterServiceOrder(0);
  }

  public clearHiddenFilters(): void {
    this.filterForm.patchValue({ priority: '', selectedUser: null });
    this.filterServiceOrder(0);
  }

  /** Limpa tudo, inclusive busca e status, e refaz a consulta sem filtros **/
  public clearAllFilters(): void {
    this.filterForm.reset({ ...this.emptyFilter, searchScope: this.filterForm.value?.searchScope ?? 'reference' });
    this.filterServiceOrder(0);
  }

  /**
   * Lista vazia não é erro: toast discreto no canto, com o motivo, em vez do overlay do Swal que
   * exigia um clique para sumir. O card vazio na tabela já diz que não há resultados.
   */
  private noResultsAlert(): void {
    // Uma linha só: o card vazio da tabela já diz "nenhuma OS encontrada"; aqui vai o motivo
    const messageKey = this.serialUnresolved
      ? 'service-order.no-results-serial'
      : this.hasActiveSearch
        ? 'service-order.no-results-filters'
        : 'service-order.no-results-none';

    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant(messageKey),
      life: 4000,
    });
  }

  /** Algum critério de busca aplicado (texto, status, prioridade ou técnico) **/
  private get hasActiveSearch(): boolean {
    const filter = this.appliedFilter;

    return !!(filter.reference || filter.installationReference || filter.status || filter.priority || filter.assignee);
  }

  public generateFileData(offset: number | null, fileType: string) {
    this.fileLoading = true;

    // Serial sem instalação: exportar com installationReference nulo traria todas as OS
    if (this.serialUnresolved) {
      this.fileLoading = false;
      this.noResultsAlert();
      return;
    }

    /** Verifica se a lista está pronta **/
    if (this.isReady) {
      /** Caso a lista esteja pronta gera o arquivo escolhido pelo usuário **/
      this.fileLoading = false;

      if (fileType === 'PDF') {
        this.downloadPDF();
      } else {
        this.downloadCSV();
      }

      return;
    }

    const currentOffset = offset ?? 0;

    this.anotherSubscription = this.serviceOrderService
      // Mesma ordenação da tela: o arquivo sai na ordem que o usuário está vendo
      .filterServiceOrder({ ...this.appliedFilter, ...this.sortParams, offset: currentOffset, limit: FILE_PAGE_SIZE + 1 })
      .valueChanges.subscribe({
        next: (response: any) => {
          /** Valida se a resposta está carregando ou não possui dados **/
          if (response.loading || !response.data) {
            return;
          }

          const rows: ServiceOrder[] = (response?.data?.serviceOrder?.edges ?? []).map(
            (edge: any) => this.mapServiceOrder(edge?.node, false),
          );

          // Preenche a lista de resultados com as OS retornadas na query, descartando a sonda
          this.FILE_DATA.push(...rows.slice(0, FILE_PAGE_SIZE));

          /** Caso seja a última página a lista está completa **/
          if (rows.length <= FILE_PAGE_SIZE) {
            this.isReady = true;
          }

          // Offset aritmético: endCursor é base64 e $offset é Int, o que quebrava a
          // exportação de qualquer resultado com mais de uma página
          this.generateFileData(currentOffset + FILE_PAGE_SIZE, fileType);
        },
        error: (error: any) => {
          this.fileLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  /** Função que realiza a conversão para arquivo CSV */
  public convertToCSV(arr: any) {
    const array = [Object?.keys(arr[0])]?.concat(arr);

    return array
      .map((it) => {
        return Object?.values(it)?.toString();
      })
      .join(',\n');
  }

  /** Função que realiza o download em CSV */
  public downloadCSV() {
    let listServiceOrder: any = [];

    this.FILE_DATA.forEach((serviceOrder) => {
      listServiceOrder.push({
        'ID OS': serviceOrder?.reference,
        'Ref. Instalação': serviceOrder?.installation?.reference,
        Status: this.translateStatus(serviceOrder?.status),
        Prioridade: this.translatePriority(serviceOrder?.priority),
        Responsável: userDisplayName(serviceOrder?.assignee) ?? '',
        'Defeitos informados': serviceOrder?.problems ?? '',
        Observação: serviceOrder?.notes ?? '',
        'Data de criação': serviceOrder?.createdAt,
      });
    });

    let csvData = this.convertToCSV(listServiceOrder);
    let fileCSV = new Blob([csvData], { type: 'text/csv' });
    let url = window.URL.createObjectURL(fileCSV);
    let a = document.createElement('a');
    a.href = url;

    a.download = `${new Date().toLocaleDateString()}-${new Date().getHours()}:${new Date().getMinutes()}-serviceorder.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);

    this.FILE_DATA = [];
    this.isReady = false;
  }

  public downloadPDF() {
    // Colunas que serão adicionadas ao header da tabela no PDF
    const tableLayout = [
      'ID OS',
      'Ref. Instalação',
      'Status',
      'Prioridade',
      'Responsável',
      'Defeitos informados',
      'Observação',
      'Data de criação',
    ];

    let content: any = [];
    // Adiciona os elementos na lista de conteúdo como um array
    this.FILE_DATA.forEach((serviceOrder) => {
      content.push([
        serviceOrder?.reference,
        serviceOrder?.installation?.reference,
        this.translateStatus(serviceOrder?.status),
        this.translatePriority(serviceOrder?.priority),
        userDisplayName(serviceOrder?.assignee) ?? '',
        serviceOrder?.problems ?? '',
        serviceOrder?.notes ?? '',
        serviceOrder?.createdAt,
      ]);
    });
    // Define a orientação da tabela (paisagem nesse caso)
    const orientation = 'l';
    // Cria o objeto da lib jsPDF com a orientação e tamanho da página
    const doc: any = new jsPDF(orientation, 'mm', 'a4');

    // Cria a tabela com o tableLayout como header e a a lista de conteúdo como corpo da tabela
    doc.autoTable({
      theme: 'grid',
      margin: { top: 20 },
      head: [tableLayout],
      body: content,
    });

    // Baixa o arquivo para o usuário, definindo o nome com a data de download
    doc.save(
      `${new Date().toLocaleDateString()}-${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}-serviceOrder.pdf`,
    );

    this.FILE_DATA = [];
    this.isReady = false;
  }

  // Função criada para tratar os dados de prioridade para o relatório
  private translatePriority(priority: string): string {
    switch (priority) {
      case 'LOW':
        return 'Baixa';

      case 'MEDIUM':
        return 'Média';

      case 'HIGH':
        return 'Alta';
    }
    return priority;
  }

  // Função criada para tratar os dados de status para o relatório
  private translateStatus(status: string): string {
    switch (status) {
      case 'OPEN':
        return 'Aberta';

      case 'CLOSED':
        return 'Finalizada';

      case 'IN_PROGRESS':
        return 'Em progresso';

      case 'CLOSED_WITH_ISSUES':
        return 'Finalizada com pendência';

      case 'DELETED':
        return 'Apagadas';
    }
    return status;
  }
}
